import * as angular from 'angular';
import './styling.scss';
import * as template from './template.html';

import { HippoApiService } from 'services/hippoApi';
import { IRootScopeService } from 'angular';
import { CurrentUserService } from 'services/currentUser';
import { ModalService } from 'common/source/services/modal';
import { paymentMethodDisplayName } from 'utilities/paymentsHelper';

import { getMomentFromHippoDateString } from 'common/source/utilities/dates';

const module = angular.module('pay-now-wrapper', []);

interface PaymentStatusBreakdownItem {
    date: string | undefined;
    value: string;
    pastDue: boolean;
}

interface PayNowWrapperController extends angular.IStrictController {
    canOnlyPayLastFailedPaymentOption: boolean;
    policyInfo: CustomerPolicyInfo;
    payNowSuccessMessage: string;
    showPayNowSection: boolean;
    paymentStatusBreakdownItem: PaymentStatusBreakdownItem;
    openDesktopPayNow: () => void;
    openMobilePayNow: () => void;
    closePayNow: () => void;
    isPayNowOpen: boolean;
    paymentMethod: string;
    showPayNowBasedOnStatus: boolean;
    setSuccessMessage: (successMessage: string, showSaveAsDefaultPaymentMethodQuestion: boolean) => void;
    showSaveAsDefaultPaymentMethodQuestion: boolean;
    isEscrowSelected: () => boolean;
    isCreditCardSelected: () => boolean;
    isAchSelected: () => boolean;
    isMobilePayNowModalOpen: boolean;
    saveCcAsDefaultPaymentMethod: () => void;
    closePayNowSection: () => void;
    showSpinner: boolean;
}

module.component('payNowWrapper', {
    bindings: {
        policyInfo: '<',
        paymentStatusBreakdownItem: '<',
        showPayNowBasedOnStatus: '<',
        paymentMethod: '<',
        isEscrowSelected: '<',
        isCreditCardSelected: '<',
        isAchSelected: '<',
        canOnlyPayLastFailedPaymentOption: '<'
    },
    template: template,
    // tslint:disable-next-line:no-big-function
    controller: function(
        this: PayNowWrapperController,
        $stateParams: PolicyStateParams,
        $rootScope: IRootScopeService,
        hippoApiService: HippoApiService,
        modalService: ModalService,
        currentUserService: CurrentUserService,
        $window: angular.IWindowService
    ) {
        this.$onInit = () => {
            this.isMobilePayNowModalOpen = false;
            updateDisplay().then(() => {
                if($stateParams.showPayNow && this.showPayNowSection) {
                    $window.innerWidth < 480 ? this.openMobilePayNow() : this.openDesktopPayNow();
                }
            });
        };

        // Close stripe edit field when another stripe edit field is opened
        $rootScope.$on('closePaymentEditSection', () => {
            this.closePayNow();
        });

        const updateDisplay = () => {
            const policyIndex = $stateParams.policyIndex;
            return currentUserService.getCustomerPolicyInfoByIndex(policyIndex).then((policyInfo) => {
                this.showPayNowSection = showPayNow(policyInfo);
            });
        };

        const showPayNow = (policyInfo = this.policyInfo) => {
            const policyEffectiveDate = getMomentFromHippoDateString(this.policyInfo.effective_date);

            return (
                (this.showPayNowBasedOnStatus &&
                    policyEffectiveDate.isSameOrBefore(new Date()) &&
                    policyInfo.next_unpaid_payment_amount > 0 &&
                    !!policyInfo.next_unpaid_payment_due_date) ||
                !!this.payNowSuccessMessage
            ); // always show pay now section if there is a success message
        };

        this.saveCcAsDefaultPaymentMethod = () => {
            this.showSpinner = true;

            const policyNumber = this.policyInfo.id;

            const policyInfoUpdate: CustomerPolicyInfoUpdate = {
                checkout_data: {
                    payment_method: 'cc'
                }
            };

            return hippoApiService
                .updateCustomerPolicy(policyNumber, policyInfoUpdate)
                .then(() => {
                    const policyIndex = $stateParams.policyIndex;
                    return currentUserService.getCustomerPolicyInfoByIndex(policyIndex);
                })
                .then((updatedPolicyInfo) => {
                    this.setSuccessMessage(
                        `Your payment method has been changed to credit card.<br>` +
                            'Future recurring payments will be made with your card ending in ' +
                            `${updatedPolicyInfo.checkout_data.cc_info.last4}.`,
                        false
                    );

                    this.showSpinner = false;
                })
                .catch(() => {
                    this.showSpinner = false;
                    // TODO: show error message if saving credit card as default payment fails.
                });
        };

        this.openDesktopPayNow = () => {
            $rootScope.$emit('closePaymentEditSection');
            this.isPayNowOpen = true;
        };

        this.openMobilePayNow = () => {
            // required to destroy the pay-now within this component when opening mobile modal
            // to avoid any errors related to duplicate components using the same controller
            this.isMobilePayNowModalOpen = true;

            $rootScope.$emit('closePaymentEditSection');
            this.isPayNowOpen = true;
            const paymentMethod = this.policyInfo.checkout_data.payment_method;
            modalService
                // this.closePayNow is passed in as a preCloseCallback
                .openFullPageModalWithPreCloseCallback(`mobile-${paymentMethod}-pay-now`, this.closePayNow, {
                    policyInfo: this.policyInfo,
                    setSuccessMessage: this.setSuccessMessage,
                    closePayNow: this.closePayNow,
                    canOnlyPayLastFailedPaymentOption: this.canOnlyPayLastFailedPaymentOption
                })
                .closePromise.then((data: any) => {});
        };

        // closes the payment method pay now component
        this.closePayNow = () => {
            // Do not close pay now if there is a success message
            if (!this.payNowSuccessMessage) {
                this.isPayNowOpen = false;
            }
        };

        // closes everything within this component except the status breakdown info
        this.closePayNowSection = () => {
            // Do not close pay now if there is a success message
            this.showPayNowSection = false;
        };

        this.setSuccessMessage = (successMessage: string, showSaveAsDefaultPaymentMethodQuestion: boolean = false) => {
            this.payNowSuccessMessage = successMessage;
            if (this.policyInfo.pending_terminate && successMessage) {
                if (this.paymentMethod === paymentMethodDisplayName.ach) {
                    this.payNowSuccessMessage +=
                        '<p>Your policy will be reinstated within 5 business days. Our team will contact you if we need more information.</p>';
                } else if (this.paymentMethod === paymentMethodDisplayName.cc) {
                    this.payNowSuccessMessage +=
                        '<p>Your policy will be reinstated within 1 business day. Our team will contact you if we need more information.</p>';
                }
            }
            this.showSaveAsDefaultPaymentMethodQuestion = showSaveAsDefaultPaymentMethodQuestion;
        };
    }
});

export { module };
